import React, { useEffect, useState } from 'react';
import { Button, Flex, DatePicker, Select, Popconfirm, Switch, Space, Tooltip } from 'antd';
import Table from 'components/Common/Table';
import { UserAddOutlined, EditOutlined, DeleteOutlined, MoreOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate } from "react-router-dom"
import * as actions from "store/actions/AuthActions"
import useUrlFilters from 'hooks/useUrlFilters';
import { message, Dropdown } from 'antd';
import dayjs from 'dayjs';

const getItemsForDropDown = (record, navigate, handleDelete, handleProfileLockToggle) => ([
  {
    key: '1',
    label: (
      <a href={`/employees/edit/${record.id}/`} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center' }}>
        <EditOutlined style={{ marginRight: '8px' }} />
        Edit
      </a>
    ),
  },
  {
    key: '2',
    label: (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 0' }}>
        <span style={{ marginRight: '12px' }}>
          {record.profile_locked ? 'Unlock Profile' : 'Lock Profile'}
        </span>
        <Switch
          size="small"
          checked={record.profile_locked}
          onChange={(checked) => handleProfileLockToggle(record.id, checked)}
          onClick={(checked, e) => e.stopPropagation()}
        />
      </div>
    ),
  },
  {
    key: "3",
    label: (
      <Popconfirm
        title="Sure to delete?"
        onConfirm={(e) => {
          e?.stopPropagation();
          handleDelete(record.id);
        }}
        onCancel={(e) => e?.stopPropagation()}
      >
        <div
          style={{ display: 'flex', alignItems: 'center' }}
          onClick={(e) => e.stopPropagation()}
        >
          <DeleteOutlined style={{ marginRight: '8px' }} />
          Delete
        </div>
      </Popconfirm>
    )
  }
])

// Filters live in the URL so a filtered list can be shared as a link.
const URL_FILTERS = {
  search: { type: 'string' },
  employee: { type: 'string' },
  department: { type: 'string' },
  role: { type: 'string' },
  joined: { type: 'dateRange', keys: ['start_date', 'end_date'] },
  page: { type: 'number', default: 1 },
  pageSize: { type: 'number', key: 'page_size', default: 50 },
};

const EmployeesTable = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const navigate = useNavigate();

  const [filters, setFilters] = useUrlFilters(URL_FILTERS);
  const searchTerm = filters.search || "";
  const selectedEmployee = filters.employee;
  const selectedDepartment = filters.department;
  const selectedRole = filters.role;
  const dateRange = filters.joined;
  const [departments, setDepartments] = useState([]);

  // Frontend pagination state
  const currentPage = filters.page;
  const setCurrentPage = (value) => setFilters({ page: value });
  const pageSize = filters.pageSize;
  const [totalRows, setTotalRows] = useState(0);

  const [allEmployees, setAllEmployees] = useState([]);
  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchAllEmployees = async () => {
    setLoading(true);
    try {
      const response = await actions.loadAllEmployees(searchTerm);

      if (response && response.status === 200) {
        const data = response.data;
        const results = data.results || [];

        // Process employee data
        const processedResults = results.map((emp) => {
          return {
            ...emp,
            full_name: emp.user_name || `${emp.user?.first_name || ''} ${emp.user?.last_name || ''}`.trim(),
            email: emp.user_email || emp.user?.email,
            joining_date: emp.date_of_joining ? dayjs(emp.date_of_joining).format('YYYY-MM-DD') : null
          };
        });

        setAllEmployees(processedResults);
        setTotalRows(data.count || processedResults.length || 0);
      } else {
        setAllEmployees([]);
        setTotalRows(0);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
      setAllEmployees([]);
      setTotalRows(0);
    }
    setLoading(false);
  };

  // Frontend pagination logic with filters
  const paginateData = () => {
    let filteredData = [...allEmployees];

    // Apply employee name filter
    if (selectedEmployee) {
      filteredData = filteredData.filter(emp => emp.full_name === selectedEmployee);
    }

    // Apply department filter
    if (selectedDepartment) {
      filteredData = filteredData.filter(emp => emp.department === selectedDepartment);
    }

    // Apply role filter
    if (selectedRole) {
      filteredData = filteredData.filter(emp => emp.job_title === selectedRole);
    }

    // Apply date range filter
    if (dateRange && dateRange[0] && dateRange[1]) {
      filteredData = filteredData.filter(emp => {
        if (!emp.date_of_joining) return false;
        const empJoinDate = dayjs(emp.date_of_joining);
        return empJoinDate.isBetween(dateRange[0], dateRange[1], 'day', '[]');
      });
    }

    setTotalRows(filteredData.length);
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedData = filteredData.slice(startIndex, endIndex);
    setDataSource(paginatedData);
  };

  // Reset to first page when filters change
  const onEmployeeChange = (employeeName) => {
    setFilters({ employee: employeeName, page: 1 });
  };

  const onDepartmentChange = (dept) => {
    setFilters({ department: dept, page: 1 });
  };

  const onRoleChange = (role) => {
    setFilters({ role, page: 1 });
  };

  const onDateRangeChange = (dates) => {
    setFilters({ joined: dates, page: 1 });
  };

  const handleDelete = async (id) => {
    const employeeToDelete = dataSource.find((item) => item.id === id);
    const response = await actions.deleteEmployees(employeeToDelete.id);
    if (response.status === 204) {
      fetchAllEmployees();
      messageApi.success('Employee deleted successfully');
    }
  };

  const handleProfileLockToggle = async (employeeId, profileLocked) => {
    try {
      const response = await actions.toggleEmployeeProfileLock(employeeId, profileLocked);
      if (response && !response.error) {
        // Update the local state to reflect the change
        setDataSource(prevData =>
          prevData.map(emp =>
            emp.id === employeeId
              ? { ...emp, profile_locked: profileLocked }
              : emp
          )
        );
        messageApi.success(`Employee profile ${profileLocked ? 'locked' : 'unlocked'} successfully`);
      } else {
        messageApi.error('Failed to update profile lock status');
      }
    } catch (error) {
      console.error('Error toggling profile lock:', error);
      messageApi.error('Failed to update profile lock status');
    }
  };

  const handleRowClick = (record) => {
    navigate(`/employees/edit/${record.id}/`);
  };

  useEffect(() => {
    // Load unique departments from employees
    const uniqueDepts = [...new Set((allEmployees || []).map(emp => emp.department).filter(Boolean))];
    setDepartments(uniqueDepts.map(dept => ({ name: dept, id: dept })));
  }, [allEmployees]);

  useEffect(() => {
    fetchAllEmployees();
  }, []);

  // Apply frontend pagination when data, filters or page change
  useEffect(() => {
    paginateData();
  }, [allEmployees, selectedEmployee, selectedDepartment, selectedRole, dateRange, currentPage, pageSize]);


  const rowSelection = {
    onChange: (selectedRowKeys, selectedRows) => {
      // console.log(`selectedRowKeys: ${selectedRowKeys}`, 'selectedRows: ', selectedRows);
    },
  }


  const defaultColumns = [
    {
      title: 'STATUS',
      dataIndex: 'employment_status',
      key: 'employment_status',
      width: 100,
      render: (status) => status || 'Active',
      filters: [
        { text: 'Active', value: 'Active' },
        { text: 'Inactive', value: 'Inactive' },
        { text: 'On Leave', value: 'On Leave' }
      ],
      onFilter: (value, record) => (record.employment_status || 'Active') === value,
      sorter: (a, b) => (a?.employment_status || 'Active').localeCompare(b?.employment_status || 'Active'),
    },
    {
      title: 'EMP ID',
      dataIndex: 'employee_id',
      key: 'employee_id',
      width: 120,
      sorter: (a, b) => (a?.employee_id || '').localeCompare(b?.employee_id || ''),
    },
    {
      title: 'NAME',
      dataIndex: 'full_name',
      key: 'full_name',
      width: 180,
      render: (text) => {
        // Filter out "None" strings and handle empty values
        if (!text || text === 'None' || text === 'null' || text === 'undefined' || text === 'N/A') {
          return '-';
        }
        // Remove any "None" words from the string
        const cleanedText = text.replace(/\bNone\b/g, '').replace(/\s+/g, ' ').trim();
        return cleanedText || '-';
      },
      sorter: (a, b) => (a?.full_name || '').localeCompare(b?.full_name || ''),
    },
    {
      title: 'EMAIL',
      dataIndex: 'email',
      key: 'email',
      width: 220,
      sorter: (a, b) => (a?.email || '').localeCompare(b?.email || ''),
    },
    {
      title: 'JOB TITLE',
      dataIndex: 'job_title',
      key: 'job_title',
      width: 150,
      sorter: (a, b) => (a?.job_title || '').localeCompare(b?.job_title || ''),
    },
    {
      title: 'DEPARTMENT',
      dataIndex: 'department',
      key: 'department',
      width: 150,
      filters: departments.map(dept => ({ text: dept.name, value: dept.name })),
      onFilter: (value, record) => record.department === value,
      sorter: (a, b) => (a?.department || '').localeCompare(b?.department || ''),
    },
    {
      title: 'EMPLOYMENT TYPE',
      dataIndex: 'employment_type',
      key: 'employment_type',
      width: 140,
      sorter: (a, b) => (a?.employment_type || '').localeCompare(b?.employment_type || ''),
    },
    {
      title: 'REPORT TO',
      dataIndex: 'report_to_name',
      key: 'report_to_name',
      width: 150,
      render: (text) => text || '-',
      sorter: (a, b) => (a?.report_to_name || '').localeCompare(b?.report_to_name || ''),
    },
    {
      title: 'MACHINE ID',
      dataIndex: 'machine_id',
      key: 'machine_id',
      width: 130,
      sorter: (a, b) => (a?.machine_id || '').localeCompare(b?.machine_id || ''),
    },
    {
      title: 'JOINING DATE',
      dataIndex: 'joining_date',
      key: 'joining_date',
      width: 120,
      align: 'right',
      sorter: (a, b) => dayjs(a?.date_of_joining || 0) - dayjs(b?.date_of_joining || 0),
    },
    {
      title: 'BIRTHDATE',
      dataIndex: 'date_of_birth',
      key: 'date_of_birth',
      width: 120,
      sorter: (a, b) => (a?.date_of_birth || '').localeCompare(b?.date_of_birth || ''),
    },
    {
      title: 'VERIFIED',
      dataIndex: 'is_verified',
      key: 'is_verified',
      width: 110,
      align: 'center',
      render: (value, record) => (
        <Tooltip title={!value ? (record.unverified_reason || 'Profile is not complete') : 'Profile is verified'}>
          <span style={{ color: value ? '#52c41a' : '#faad14', fontWeight: 500, cursor: 'pointer' }}>
            {value ? 'Verified' : 'Unverified'}
          </span>
        </Tooltip>
      ),
      sorter: (a, b) => (a?.is_verified === b?.is_verified ? 0 : a?.is_verified ? -1 : 1),
    },
    {
      title: 'PROFILE LOCKED',
      dataIndex: 'profile_locked',
      key: 'profile_locked',
      width: 130,
      align: 'center',
      render: (value) => (
        <span style={{ color: value ? '#f5222d' : '#52c41a', fontWeight: 500 }}>
          {value ? 'Locked' : 'Unlocked'}
        </span>
      ),
      sorter: (a, b) => (a?.profile_locked === b?.profile_locked ? 0 : a?.profile_locked ? -1 : 1),
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      key: 'actions',
      width: 80,
      render: (_, record) => {
        return dataSource?.length >= 1 ? (
          <Flex gap="middle" style={{ padding: 5 }}>
            <Dropdown
              menu={{ items: getItemsForDropDown(record, navigate, handleDelete, handleProfileLockToggle) }}
              placement="bottomLeft"
              onClick={(e) => e.stopPropagation()}
            >
              <MoreOutlined onClick={(e) => e.stopPropagation()} />
            </Dropdown>
          </Flex>
        ) : null
      }
    },
  ];

  const handleSave = (row) => {
    const newData = [...dataSource];
    const index = newData.findIndex((item) => row.key === item.key);
    const item = newData[index];
    newData.splice(index, 1, {
      ...item,
      ...row,
    });
    setDataSource(newData);
  };


  const columns = defaultColumns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record) => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave,
      }),
    };
  });

  const [selectionType] = useState('checkbox');

  return (
    <div style={{ padding: '24px', background: '#f5f5f5', minHeight: '100vh' }}>
      {contextHolder}

      {/* Header with Filters */}
      <div style={{
        background: 'white',
        padding: '20px 24px',
        borderRadius: '8px',
        marginBottom: '16px',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)'
      }}>
        <Flex gap='middle' justify='space-between' wrap="wrap">
          <Flex gap='middle' align='center' wrap="wrap" style={{ flex: 1 }}>
            <Select
              placeholder="Employee Name"
              value={selectedEmployee}
              onChange={onEmployeeChange}
              style={{ width: 200 }}
              showSearch
              allowClear
              optionFilterProp="children"
            >
              {allEmployees.map((emp) => (
                <Select.Option key={emp.id} value={emp.full_name}>
                  {emp.full_name}
                </Select.Option>
              ))}
            </Select>

            <Select
              placeholder="Department"
              value={selectedDepartment}
              onChange={onDepartmentChange}
              style={{ width: 180 }}
              showSearch
              allowClear
            >
              {departments.map((dept) => (
                <Select.Option key={dept.id} value={dept.name}>
                  {dept.name}
                </Select.Option>
              ))}
            </Select>

            <Select
              placeholder="Role"
              value={selectedRole}
              onChange={onRoleChange}
              style={{ width: 180 }}
              showSearch
              allowClear
            >
              {[...new Set(allEmployees.map(emp => emp.job_title).filter(Boolean))].map((role) => (
                <Select.Option key={role} value={role}>
                  {role}
                </Select.Option>
              ))}
            </Select>

            <DatePicker.RangePicker
              placeholder={['Start Date', 'End Date']}
              value={dateRange}
              onChange={onDateRangeChange}
              style={{ width: 280 }}
              allowClear
            />

            <Tooltip title="Refresh">
              <Button
                icon={<ReloadOutlined />}
                onClick={() => fetchAllEmployees()}
                loading={loading}
                aria-label="Refresh"
              />
            </Tooltip>
          </Flex>

          <Button
            onClick={() => navigate("/employees/new")}
            icon={<UserAddOutlined />}
            type="primary"
            size="large"
          >
            Add Employee
          </Button>
        </Flex>
      </div>

      {/* Table Section */}
      <div style={{
        background: 'white',
        padding: '24px',
        borderRadius: '8px',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)'
      }}>
        <h2 style={{
          fontSize: '20px',
          fontWeight: 600,
          marginBottom: '20px',
          color: '#262626'
        }}>
          Employee List
        </h2>
        <Flex>
          <Table
            size="middle"
            rowSelection={{
              type: selectionType,
              ...rowSelection,
            }}
            loading={loading}
            onRow={(record) => ({
              onClick: () => handleRowClick(record),
              style: { cursor: 'pointer' }
            })}
            dataSource={dataSource}
            columns={columns}
            rowKey={row => row.id}
            pagination={false}
            scroll={{ x: 1260, y: 500 }}
          />
        </Flex>

        {/* Custom Pagination */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '16px',
          padding: '16px 0'
        }}>
          <span style={{ color: '#666', fontSize: '14px' }}>
            Page {currentPage} of {Math.ceil(totalRows / pageSize) || 1}
          </span>
          <Space>
            <Button
              onClick={() => {
                if (currentPage > 1) {
                  setCurrentPage(currentPage - 1);
                }
              }}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            <Button
              onClick={() => {
                if (currentPage < Math.ceil(totalRows / pageSize)) {
                  setCurrentPage(currentPage + 1);
                }
              }}
              disabled={currentPage >= Math.ceil(totalRows / pageSize)}
            >
              Next
            </Button>
          </Space>
        </div>
      </div>

    </div>
  );
};
export default EmployeesTable;